import { Control, Controller } from "react-hook-form";
import { RadioGroup, RadioGroupItem } from "../../../../components/ui/common/radio-group";
import { cn } from "../../../../lib/utils";
import { UserFormData, UserRoleOption } from "../types/user.types";

/** Helper line under each known role name; unknown roles simply render without one. */
const ROLE_DESCRIPTIONS: Record<string, string> = {
  "Group admin": "Access across company groups",
  Admin: "Manages one company",
  User: "Standard product and process access",
  "User (recipe)": "Recipe workflows only",
  Supplier: "Supplier portal access"
};

interface UserRoleSelectorProps {
  control: Control<UserFormData>;
  roles: UserRoleOption[];
  disabled?: boolean;
}

/** Single-choice role cards bound to the form's `role` field. */
const UserRoleSelector = ({ control, roles, disabled }: UserRoleSelectorProps) => (
  <Controller
    control={control}
    name="role"
    render={({ field }) => (
      <RadioGroup
        value={field.value}
        onValueChange={field.onChange}
        disabled={disabled}
        className="tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2"
      >
        {roles.map(role => {
          const id = `user-role-${role.key}`;
          const selected = field.value === role.text;
          return (
            <label
              key={role.key}
              htmlFor={id}
              className={cn(
                "tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-bg-white tw-p-4",
                selected ? "tw-border-primary tw-bg-primary/10" : "tw-border-border",
                disabled && "tw-cursor-not-allowed tw-opacity-70"
              )}
            >
              <RadioGroupItem id={id} value={role.text} className="tw-mt-1 tw-shrink-0" />
              <span className="tw-flex tw-flex-col">
                <span className="tw-text-base tw-font-medium tw-text-foreground">{role.text}</span>
                {ROLE_DESCRIPTIONS[role.text] ? (
                  <span className="tw-text-sm tw-text-muted-foreground">{ROLE_DESCRIPTIONS[role.text]}</span>
                ) : null}
              </span>
            </label>
          );
        })}
      </RadioGroup>
    )}
  />
);

export default UserRoleSelector;
